import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './BarRestaurantTableDetailModal.css';
import AppIcon from './icons/AppIcon';

export default function BarRestaurantTableDetailModal({ table, onClose }) {
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [serviceTax, setServiceTax] = useState({ percentage: 0, enabled: false });
  const [serviceTaxLoaded, setServiceTaxLoaded] = useState(false);

  useEffect(() => {
    if (table?.id) {
      loadTableData();
    }
  }, [table?.id]);

  useEffect(() => {
    if (tableData?.project_id) {
      loadServiceTax();
    }
  }, [tableData?.project_id]);

  const loadTableData = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`/api/bar-restaurant/table/${table.id}`);
      if (!response.ok) throw new Error('Erro ao buscar mesa');
      
      const data = await response.json();
      if (data.success && data.table) {
        setTableData(data.table);
      } else {
        throw new Error('Dados da mesa não encontrados');
      }
    } catch (error) {
      console.error('[BarRestaurantTableDetailModal] Erro ao carregar mesa:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleServiceTax = async (enabled) => {
    if (!tableData?.id) return;

    try {
      setUpdating(true);
      const response = await fetch(`/api/bar-restaurant/table/${tableData.id}/service-tax`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ removed: !enabled })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Erro ao atualizar taxa');
      }

      setTableData((prev) => ({
        ...prev,
        metadata: result.table?.metadata || prev.metadata
      }));
    } catch (error) {
      console.error('[BarRestaurantTableDetailModal] Erro ao atualizar taxa:', error);
      alert('Erro ao atualizar taxa: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const updateOrderItems = async (orderId, items) => {
    try {
      setUpdating(true);
      const response = await fetch(`/api/bar-restaurant/order/${orderId}/items`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Erro ao atualizar itens');
      }

      setTableData((prev) => ({
        ...prev,
        pedidos: (prev.pedidos || []).map((pedido) =>
          pedido.id === orderId
            ? { ...pedido, order_payload: result.order?.order_payload || pedido.order_payload }
            : pedido
        )
      }));
    } catch (error) {
      console.error('[BarRestaurantTableDetailModal] Erro ao atualizar itens:', error);
      alert('Erro ao atualizar itens: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleChangeItemQty = (pedido, itemIndex, delta) => {
    const payload = pedido.order_payload || {};
    const itens = payload.itens || [];
    const updatedItems = itens.map((item, idx) => {
      if (idx !== itemIndex) return item;
      const novaQtd = Math.max(0, Number(item.quantidade || 0) + delta);
      return { ...item, quantidade: novaQtd };
    });

    return updateOrderItems(pedido.id, updatedItems);
  };

  const handleRemoveItem = (pedido, itemIndex) => {
    const payload = pedido.order_payload || {};
    const itens = payload.itens || [];
    const updatedItems = itens.filter((_, idx) => idx !== itemIndex);
    return updateOrderItems(pedido.id, updatedItems);
  };

  const loadServiceTax = async () => {
    try {
      setServiceTaxLoaded(false);
      const response = await fetch(`/api/bar-restaurant/service-tax/${tableData.project_id}`);
      if (!response.ok) throw new Error('Erro ao buscar taxa de serviço');

      const data = await response.json();
      if (data.success) {
        setServiceTax({
          percentage: Number(data.serviceTax?.percentage || 0),
          enabled: Boolean(data.serviceTax?.enabled)
        });
      }
    } catch (error) {
      console.error('[BarRestaurantTableDetailModal] Erro ao carregar taxa:', error);
    } finally {
      setServiceTaxLoaded(true);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR');
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `Há ${diffMins} min`;
    if (diffHours < 24) return `Há ${diffHours}h`;
    return formatDate(timestamp);
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'pedidos_em_aberto': 'Pedidos em aberto',
      'em_producao': 'Em Produção',
      'pronto': 'Pronto',
      'entregue': 'Entregue',
      'cancelado': 'Cancelado'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'pedidos_em_aberto': '#fef3c7',
      'em_producao': '#fef3c7',
      'pronto': '#d1ecf1',
      'entregue': '#d4edda',
      'cancelado': '#f8d7da'
    };
    return colorMap[status] || '#e2e3e5';
  };

  const handleCloseTable = async () => {
    if (!tableData?.id) return;
    
    try {
      setUpdating(true);
      const response = await fetch('/api/bar-restaurant/table/close', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tableId: tableData.id })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Erro ao fechar mesa');
      }
      
      console.log('[BarRestaurantTableDetailModal] Mesa fechada com sucesso');
      onClose();
    } catch (error) {
      console.error('[BarRestaurantTableDetailModal] Erro ao fechar mesa:', error);
      alert('Erro ao fechar mesa: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleArchiveTable = async () => {
    if (!tableData?.id) return;
    
    try {
      setUpdating(true);
      const { error } = await supabase
        .from('bar_restaurant_tables')
        .update({ status: 'arquivada' })
        .eq('id', tableData.id);

      if (error) throw error;
      
      console.log('[BarRestaurantTableDetailModal] Mesa arquivada com sucesso');
      onClose();
    } catch (error) {
      console.error('[BarRestaurantTableDetailModal] Erro ao arquivar mesa:', error);
      alert('Erro ao arquivar mesa: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const pedidos = tableData?.pedidos || [];
  const subtotalMesa = pedidos.reduce((sum, pedido) => {
    const payload = pedido.order_payload || {};
    return sum + Number(payload.total || 0);
  }, 0);
  const serviceTaxRemoved = Boolean(tableData?.metadata?.service_tax_removed);
  const serviceTaxEnabled = serviceTax.enabled && !serviceTaxRemoved;
  const serviceTaxAmount = serviceTaxEnabled
    ? (subtotalMesa * (Number(serviceTax.percentage) || 0)) / 100
    : 0;
  const totalComTaxa = subtotalMesa + serviceTaxAmount;

  if (loading) {
    return (
      <div className="bar-restaurant-modal-overlay" onClick={onClose}>
        <div className="bar-restaurant-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">
            <AppIcon name="progress_activity" className="spinning" />
          </div>
        </div>
      </div>
    );
  }

  if (!tableData) {
    return (
      <div className="bar-restaurant-modal-overlay" onClick={onClose}>
        <div className="bar-restaurant-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-error">
            <p>Erro ao carregar dados da mesa</p>
            <button onClick={onClose} className="btn-close">Fechar</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bar-restaurant-modal-overlay" onClick={onClose}>
      <div className="bar-restaurant-modal table-modal bar-restaurant-table-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-title">
            <AppIcon name="table_restaurant" />
            <h2>Mesa {tableData.numero_mesa}</h2>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        {/* Informações da Mesa */}
        <div className="table-modal-content">
          <div className="table-modal-body">
            <div className="table-info-section">
              <h3>Informações da Mesa</h3>
              <div className="info-grid">
                <div className="info-card">
                  <span className="info-label">Status</span>
                  <span className={`info-value status-badge ${tableData.status === 'fechada' ? 'fechada' : 'aberta'}`}>
                    ● {tableData.status === 'fechada' ? 'Fechada' : 'Aberta'}
                  </span>
                </div>
                <div className="info-card">
                  <span className="info-label">Pedidos em Aberto</span>
                  <span className="info-value">{tableData.pedidos_abertos}</span>
                </div>
                <div className="info-card">
                  <span className="info-label">Total em Aberto</span>
                  <span className="info-value">R$ {subtotalMesa.toFixed(2)}</span>
                </div>
                <div className="info-card">
                  <span className="info-label">Aberta há</span>
                  <span className="info-value">{getTimeAgo(tableData.primeira_ordem_em)}</span>
                </div>
              </div>

              {serviceTaxLoaded && serviceTax.enabled && (
                <div className="table-summary">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <strong>R$ {subtotalMesa.toFixed(2)}</strong>
                  </div>
                  <div className="summary-row">
                    <span>Taxa de serviço ({Number(serviceTax.percentage || 0)}%)</span>
                    <strong>R$ {serviceTaxAmount.toFixed(2)}</strong>
                  </div>
                  <div className="summary-row total">
                    <span>Total da Mesa</span>
                    <strong>R$ {totalComTaxa.toFixed(2)}</strong>
                  </div>
                  <label className="service-tax-toggle">
                    <input
                      type="checkbox"
                      checked={!serviceTaxRemoved}
                      disabled={updating || tableData.status === 'fechada'}
                      onChange={(event) => handleToggleServiceTax(event.target.checked)}
                    />
                    Aplicar taxa de serviço nesta mesa
                  </label>
                </div>
              )}
            </div>

            {/* Pedidos da Mesa */}
            <div className="orders-section">
              <h3>Pedidos da Mesa ({tableData.pedidos?.length || 0})</h3>
              {tableData.pedidos && tableData.pedidos.length > 0 ? (
                <div className="orders-list">
                  {tableData.pedidos.map((pedido) => {
                    const payload = pedido.order_payload || {};
                    const statusAtual = pedido.status_atual || 'pedidos_em_aberto';
                    const itens = payload.itens || [];

                    return (
                      <div key={pedido.id} className="order-item-detail">
                        <div className="order-header-detail">
                          <span className="order-id">Pedido #{pedido.id.substring(0, 8).toUpperCase()}</span>
                          <span 
                            className="order-status-badge"
                            style={{ backgroundColor: getStatusColor(statusAtual) }}
                          >
                            {getStatusLabel(statusAtual)}
                          </span>
                        </div>

                        <div className="order-items-list">
                          {itens.map((item, idx) => (
                            <div key={idx} className="order-item-row">
                              <span className="item-qty">x{item.quantidade}</span>
                              <span className="item-name">{item.nome}</span>
                              <span className="item-price">R$ {item.preco_total?.toFixed(2) || '0.00'}</span>
                              <div className="item-actions">
                                <button
                                  type="button"
                                  className="item-action-btn"
                                  onClick={() => handleChangeItemQty(pedido, idx, -1)}
                                  disabled={updating || tableData.status === 'fechada'}
                                >
                                  -
                                </button>
                                <button
                                  type="button"
                                  className="item-action-btn"
                                  onClick={() => handleChangeItemQty(pedido, idx, 1)}
                                  disabled={updating || tableData.status === 'fechada'}
                                >
                                  +
                                </button>
                                <button
                                  type="button"
                                  className="item-action-btn danger"
                                  onClick={() => handleRemoveItem(pedido, idx)}
                                  disabled={updating || tableData.status === 'fechada'}
                                >
                                  <AppIcon name="delete" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="order-total-row">
                          <span className="total-label">Total do Pedido:</span>
                          <span className="total-value">R$ {payload.total?.toFixed(2) || '0.00'}</span>
                        </div>

                        <div className="order-time">
                          <AppIcon name="schedule" />
                          <span>{formatTime(pedido.created_at)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-orders">
                  <p>Nenhum pedido nesta mesa</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer com Ações */}
        <div className="modal-footer">
          <button 
            className="btn-action btn-close-table" 
            onClick={handleCloseTable}
            disabled={updating || tableData.status === 'fechada'}
          >
            <AppIcon name="lock" />
            {tableData.status === 'fechada' ? 'Mesa Fechada' : 'Fechar Mesa'}
          </button>
          <button 
            className="btn-action btn-archive-table"
            onClick={handleArchiveTable}
            disabled={updating}
          >
            <AppIcon name="archive" />
            Arquivar
          </button>
          <button className="btn-action btn-cancel" onClick={onClose} disabled={updating}>
            <AppIcon name="close" />
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
